import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLazyGetAddressGeolocateQuery, useLazyGetAddressSelectQuery } from "../../../middleware/addressDataApi";
import { useAppSelector } from "../../../hooks/redux";
import { Autocomplete, Box, Checkbox, FormControlLabel } from "@mui/material";
import InputText from "../InputText";
import CheckBoxChecked from "../icons/CheckBoxChecked";
import { YANDEX_MAPS_DATA } from "../../../constants";
import { Map, Placemark, YMaps, ZoomControl } from "@pbe/react-yandex-maps";
import CheckBox from "../icons/CheckBox";
import DropdownIcon from "../icons/DropdownIcon";

export const AddressInputNew = ({
  label,
  disabled = false,
  onChange,
  error,
  value,
  placeholder,
  shouldSetCoordinates,
  isShowPointOnMap = true,
  small = false,
  isCity = true, // Новый пропс для режима "только город"
  ...rest
}) => {
  const zoomLevel = 18;
  const [inputValue, setInputValue] = useState("");
  const [isMapVisible, setIsMapVisible] = useState(false);
  const [coordinates, setCoordinates] = useState([55.7558, 37.6173]);
  const [isOpen, setIsOpen] = useState(false);
  const [internalValue, setInternalValue] = useState(null);
  const mapRef = useRef(null);
  const { t } = useTranslation();

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const [getAddressGeoLocate] = useLazyGetAddressGeolocateQuery();
  // const [getAddressById] = useLazyFindByIdQuery();
  const [getAddressSelect] = useLazyGetAddressSelectQuery();
  const { addressData } = useAppSelector((state) => state.addressData);

  const getFilteredAddressSuggestions = (suggestions) => {
    if (!isCity || !suggestions) return suggestions;

    return suggestions.filter((suggestion) => {
      const { data } = suggestion;
      return (
        data.region_type_full &&
        (data.region_type_full.includes("область") ||
          data.region_type_full.includes("край") ||
          data.region_type_full.includes("республика") ||
          (data.city_with_type && !data.street_with_type))
      );
    });
  };

  const filteredAddressData = getFilteredAddressSuggestions(addressData?.suggestions);

  const onPlacemarkDragEnd = (event) => {
    if (isCity) return;

    const coords = event.get("target").geometry._coordinates;
    setCoordinates(coords);
    getAddressGeoLocate({ lat: coords[0], lon: coords[1] })
      .then(({ data }) => {
        if (data.suggestions.length > 0) {
          const requestValue = getRequestNewValue(data.suggestions[0]);
          onChange(requestValue);
          setInputValue(formatDisplayValue(requestValue));
          setInternalValue(data.suggestions[0]);
          mapRef.current.setCenter(coords, zoomLevel);
        }
      })
      .catch((error) => console.error("Ошибка:", error));
  };

  const handleMapClick = (event) => {
    if (isCity) return; // В режиме города не позволяем кликать по карте

    const coords = event.get("coords");
    setCoordinates(coords);
    getAddressGeoLocate({ lat: coords[0], lon: coords[1] })
      .then(({ data }) => {
        if (data.suggestions.length > 0) {
          const requestValue = getRequestNewValue(data.suggestions[0]);
          onChange(requestValue);
          setInputValue(formatDisplayValue(requestValue));
          setInternalValue(data.suggestions[0]);
          mapRef.current.setCenter(coords, zoomLevel);
        }
      })
      .catch((error) => console.error("Ошибка:", error));
  };

  const getRequestNewValue = (newValue) => {
    const { data } = newValue || {};

    if (isCity) {
      return {
        subdivision: data?.region_with_type || "",
        region: data?.region_with_type || "",
        area: data?.area_with_type || "",
        city: data?.city_with_type || data?.city || data?.settlement_with_type || "",
        cityDistrict: data?.city_district_with_type || "",
        cityArea: data?.city_area || "",
        town: data?.settlement_with_type || "",
        street: "",
        building: "",
        extension: "",
        index: "",
        district: data?.federal_district || "",
        coordinates: shouldSetCoordinates
          ? {
              latitude: data?.geo_lat || 0,
              longitude: data?.geo_lon || 0,
            }
          : null,
      };
    }

    return {
      subdivision: data?.region_with_type || "",
      region: data?.region_with_type || "",
      area: data?.area_with_type || "",
      city: data?.city_with_type || data?.city || "",
      town: data?.settlement_with_type || "",
      street: data?.street_with_type || data?.street || "",
      building: data?.house || "",
      extension: data?.block || "",
      index: data?.postal_code || "",
      district: data?.federal_district || "",
      cityDistrict: data?.city_district_with_type || "",
      cityArea: data?.city_area || "",
      coordinates: shouldSetCoordinates
        ? {
            latitude: data?.geo_lat || 0,
            longitude: data?.geo_lon || 0,
          }
        : null,
    };
  };

  const formatDisplayValue = (val) => {
    if (!val) return "";

    if (isCity) {
      const parts = [];
      if (val.region) parts.push(val.region);
      if (val.area && val.area !== val.region) parts.push(val.area);
      if (val.city && val.city !== val.region) parts.push(val.city);
      if (val.cityDistrict) parts.push(val.cityDistrict);
      if (val.cityArea) parts.push(val.cityArea);
      if (val.town && val.town !== val.city) parts.push(val.town);
      return parts.join(", ");
    }

    const parts = [];
    if (val.index) parts.push(val.index);
    if (val.region) parts.push(val.region);
    if (val.area && val.area !== val.region) parts.push(val.area);
    if (val.city && val.city !== val.region) parts.push(val.city);
    if (val.town && val.town !== val.city) parts.push(val.town);
    if (val.street) parts.push(val.street);
    if (val.building) parts.push(val.building);
    if (val.extension) parts.push(val.extension);

    return parts.join(" ");
  };

  const removeIndexFromQuery = (query) => {
    if (!query) return "";
    return query.replace(/^\d{6}\s*,?\s*/, "");
  };

  useEffect(() => {
    if (
      value &&
      (value.city ||
        value.street ||
        value.building ||
        value.index ||
        value.region ||
        value.town ||
        value.subdivision ||
        value.area ||
        value.city_district ||
        value.city_area)
    ) {
      const coords =
        value.coordinates?.latitude && value.coordinates?.longitude
          ? [value.coordinates?.latitude, value.coordinates?.longitude]
          : coordinates;
      setCoordinates(coords);
      mapRef?.current?.setCenter(coords, zoomLevel);
      const formattedValue = {
        data: {
          region_with_type: value.subdivision || value.region,
          area_with_type: value.area || "", // Добавлено поле area_with_type
          city_with_type: value.city,
          city: value.city,
          settlement_with_type: value.town,
          street_with_type: value.street,
          street: value.street,
          house: value.building,
          block: value.extension,
          postal_code: value.index,
          federal_district: value.district,
          city_district_with_type: value.city_district || "", // Добавлено поле city_district_with_type
          city_area_with_type: value.city_area || "", // Добавлено поле city_area_with_type
          geo_lat: value.coordinates?.latitude,
          geo_lon: value.coordinates?.longitude,
        },
        unrestricted_value: "",
        value: formatDisplayValue(value),
      };
      setInternalValue(formattedValue);
      setInputValue(formatDisplayValue(value));
    } else {
      setInternalValue(null);
      setInputValue("");
    }
  }, [value]);

  useEffect(() => {
    let active = true;
    if (inputValue === "") {
      getAddressSelect("");
      return undefined;
    }

    if (active && inputValue !== formatDisplayValue(value)) {
      // Удаляем индекс из поискового запроса перед отправкой в API
      const queryWithoutIndex = removeIndexFromQuery(inputValue);
      getAddressSelect(queryWithoutIndex);
    }

    return () => {
      active = false;
    };
  }, [inputValue, getAddressSelect, value]);

  return (
    <div>
      <Box
        display="flex"
        gap={"16px"}
        flexDirection={small ? "column" : "row"}
        sx={{ flexFlow: small ? "column-reverse" : "row" }}
      >
        <Box sx={{ width: "100%" }}>
          <Autocomplete
            id="address"
            getOptionLabel={(option) => option.value}
            options={filteredAddressData || []} // Используем отфильтрованные данные
            filterOptions={(x) => x}
            autoComplete
            includeInputInList
            filterSelectedOptions
            disabled={disabled}
            noOptionsText={isCity ? t("Введите регион или город") : t("Введите адрес")}
            open={isOpen}
            onOpen={handleOpen}
            onClose={handleClose}
            value={internalValue}
            inputValue={inputValue}
            IconComponent={() => <Box>{isOpen ? <DropdownIcon /> : <DropdownIcon />}</Box>}
            onChange={(event, newValue) => {
              if (newValue) {
                const requestValue = getRequestNewValue(newValue);
                onChange(requestValue);
                setInputValue(formatDisplayValue(requestValue));
                setInternalValue(newValue);
              }
            }}
            onInputChange={(event, newInputValue, reason) => {
              setInputValue(newInputValue);
            }}
            {...rest}
            renderInput={(params) => (
              <InputText
                placeholder={isCity ? "Введите регион или город" : placeholder}
                {...params}
                type={"text"}
                label={label}
                disabled={disabled}
                error={error}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: !disabled && (
                    <>
                      <Box
                        sx={{ cursor: "pointer" }}
                        onClick={() => {
                          setIsOpen((prev) => !prev);
                        }}
                      >
                        {isOpen ? <DropdownIcon /> : <DropdownIcon />}
                      </Box>
                    </>
                  ),
                }}
              />
            )}
          />
        </Box>

        {isShowPointOnMap &&
          !isCity && ( // Скрываем карту в режиме города
            <FormControlLabel
              control={
                <Checkbox
                  checked={isMapVisible}
                  onChange={() => setIsMapVisible(!isMapVisible)}
                  sx={{
                    borderRadius: 0,
                    mr: "8px",
                    "& .MuiCheckbox-root": { paddingTop: "24px !important" },
                  }}
                  icon={
                    <Box>
                      <CheckBox />
                    </Box>
                  }
                  checkedIcon={
                    <Box sx={{ color: "var(--headers)" }}>
                      <CheckBoxChecked />
                    </Box>
                  }
                />
              }
              sx={{ marginTop: "auto", mr: "0", width: "fit-content !important" }}
              label="Указать точку на карте"
            />
          )}
      </Box>

      {isMapVisible &&
        !isCity && ( // Скрываем карту в режиме города
          <YMaps query={YANDEX_MAPS_DATA.query}>
            <Map
              modules={YANDEX_MAPS_DATA.modules}
              instanceRef={mapRef}
              defaultState={{ center: coordinates, zoom: 10 }}
              width="100%"
              height="400px"
              onClick={handleMapClick}
            >
              <ZoomControl options={{ float: "left" }} />
              <Placemark geometry={coordinates} options={{ draggable: true }} onDragEnd={onPlacemarkDragEnd} />
            </Map>
          </YMaps>
        )}
    </div>
  );
};
